import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "frontend-components/lib/utils";

const buttonVariants = cva(
    "group/button focus-visible:ring-ring/50 inline-flex shrink-0 items-center justify-center border whitespace-nowrap outline-none transition-[color,border-color,background-color,filter] duration-300 ease-out select-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    {
        variants: {
            variant: {
                outline:
                    "rounded-pill border-compendia-black bg-transparent text-compendia-black hover:bg-compendia-black/5",
                fill: "rounded-pill border-compendia-black bg-compendia-black text-white hover:brightness-125",
                submit: "rounded-submit border-transparent bg-compendia-black text-white hover:bg-compendia-gray-80",
                arrow: "rounded-pill border-compendia-black bg-transparent text-compendia-black hover:bg-compendia-black/5",
                "outline-white":
                    "rounded-pill border-white/60 bg-transparent text-white hover:bg-white/10",
                "fill-white":
                    "rounded-pill border-white bg-white text-compendia-black hover:brightness-95",
                // Internal chrome only — dialog close, combobox clear, input-group addons.
                // Not a Compendia brand button; never use for page-level actions.
                ghost: "rounded-pill border-transparent bg-transparent text-compendia-black hover:bg-compendia-black/5",
            },
            size: {
                default: "px-5 py-2.5 text-[0.95rem] font-medium",
                submit: "px-7 py-3 text-[0.95rem] font-medium",
                arrow: "size-[42px] p-0",
                "icon-xs": "size-6 p-0 text-xs",
                "icon-sm": "size-8 p-0",
            },
        },
        compoundVariants: [
            { variant: "submit", size: "default", class: "px-7 py-3" },
            { variant: "arrow", size: "default", class: "size-[42px] p-0" },
        ],
        defaultVariants: {
            variant: "outline",
            size: "default",
        },
    },
);

function Button({
    className,
    variant = "outline",
    size = "default",
    ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
    return (
        <ButtonPrimitive
            data-slot="button"
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    );
}

export { Button, buttonVariants };
